import * as React from 'react';
import { baseURL } from 'src/api/client';
import { APIURLS } from 'src/api/urls';
import {
  clearCitizenAuth,
  getOverrideApiUrl,
  isDevBypassAuth,
  LS_API_URL,
  LS_DEV_BYPASS_AUTH,
  overrideApiUrl,
  setDevBypassAuth,
} from 'src/api/state';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { isLocalhost, isVercel } from 'src/utils';
import { getCrmHostname } from 'src/app/brandColorCache';

const DEV_UNLOCK_KEY = 'wh_dev_unlocked';
const UNLOCK_EVENT = 'wh:dev-unlocked';
const UNLOCK_QUERY_PARAM = 'dev';

// SHA-256 of the secret passphrase. Visit `?dev=<passphrase>` to unlock the
// indicator in prod builds. Change this hash (and the passphrase you share
// with staff) to rotate access. The plain passphrase is intentionally not in
// source — only the hash, so casual readers of the bundle can't lift it.
const UNLOCK_HASH =
  'c0b88c744e44d629b2fd8b48d32b90f0180211b1e91f873c0395e01ac29dd82f';

// Mirrors src/app/Company.store.tsx — must stay in sync.
const TENANT_LS_KEY = 'DEV_CRM_HOSTNAME';
const DEFAULT_TENANT = 'portal.wastehero.io';

type Tone = 'neutral' | 'info' | 'warn' | 'danger';

type EnvOption = {
  value: string;
  label: string;
  short: string;
  tone: Tone;
};

const ENV_OPTIONS: EnvOption[] = [
  {
    value: APIURLS.production,
    label: 'Production',
    short: 'prod',
    tone: 'neutral',
  },
  {
    value: APIURLS.development,
    label: 'Development',
    short: 'dev',
    tone: 'warn',
  },
  {
    value: APIURLS.preview,
    label: 'Preview',
    short: 'preview',
    tone: 'info',
  },
];

const PREVIEW_COUNT = 6;
const PREVIEW_REGEX = /preview-\d+/;

const buildPreviewUrl = (n: number): string => {
  const base = APIURLS.preview;
  if (!base || base === 'not-set') return base;
  return base.replace(PREVIEW_REGEX, `preview-${n}`);
};

const getPreviewNumber = (url: string): number | null => {
  const match = url.match(/preview-(\d+)/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
};

const isPreviewUrl = (url: string): boolean => {
  if (!APIURLS.preview || APIURLS.preview === 'not-set') return false;
  // Compare against the configured preview URL with the number stripped — so
  // any preview-N variant matches.
  const previewShape = APIURLS.preview.replace(PREVIEW_REGEX, 'preview-N');
  const candidateShape = url.replace(PREVIEW_REGEX, 'preview-N');
  return previewShape === candidateShape && PREVIEW_REGEX.test(url);
};

const TENANT_EXAMPLES = [
  'portal-development.wastehero.io',
  'hra-portal.wastehero.io',
  'fredericia-portal.wastehero.io',
];

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground border-border',
  info: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950/60 dark:text-blue-100 dark:border-blue-800',
  warn: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/60 dark:text-amber-100 dark:border-amber-800',
  danger:
    'bg-red-100 text-red-900 border-red-300 dark:bg-red-950/60 dark:text-red-100 dark:border-red-800',
};

const matchEnv = (url: string): EnvOption | undefined => {
  if (isPreviewUrl(url)) {
    return ENV_OPTIONS.find((opt) => opt.short === 'preview');
  }
  return ENV_OPTIONS.find((opt) => opt.value && opt.value === url);
};

const subdomain = (host: string) => host.split('.')[0] || host;

const sha256Hex = async (value: string): Promise<string> => {
  const buf = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const stripUnlockParam = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete(UNLOCK_QUERY_PARAM);
  const search = url.searchParams.toString();
  const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  window.history.replaceState({}, '', next);
};

const useUnlocked = () => {
  const [unlocked, setUnlocked] = React.useState(
    () =>
      typeof window !== 'undefined' &&
      sessionStorage.getItem(DEV_UNLOCK_KEY) === '1'
  );

  // Subscribe to in-tab unlock events.
  React.useEffect(() => {
    const handler = () => setUnlocked(true);
    window.addEventListener(UNLOCK_EVENT, handler);
    return () => window.removeEventListener(UNLOCK_EVENT, handler);
  }, []);

  // Check ?dev=<passphrase> on mount.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const candidate = params.get(UNLOCK_QUERY_PARAM);
    if (!candidate) return;

    let cancelled = false;
    void (async () => {
      try {
        const hash = await sha256Hex(candidate);
        if (cancelled) return;
        if (hash === UNLOCK_HASH) {
          sessionStorage.setItem(DEV_UNLOCK_KEY, '1');
          setUnlocked(true);
        }
      } finally {
        // Always scrub the param so it doesn't linger in history/logs,
        // even on a wrong guess.
        if (!cancelled) stripUnlockParam();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return unlocked;
};

const getCurrentTenant = (): string =>
  (typeof window !== 'undefined' && localStorage.getItem(TENANT_LS_KEY)) ||
  DEFAULT_TENANT;

export const DevBackendIndicator: React.FC = () => {
  const unlocked = useUnlocked();
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(baseURL);
  const [tenantInput, setTenantInput] = React.useState(getCrmHostname);
  const [bypassInput, setBypassInput] = React.useState(isDevBypassAuth);

  const isDev = import.meta.env.MODE === 'development';
  // Auto-show on Vercel preview deployments (internal QA URLs). The production
  // custom domain is always excluded (isVercel is false there). REACT_APP_
  // ENVIRONMENT is wired from Vercel's per-deploy VERCEL_ENV in the build:vercel
  // script ('preview' on previews, 'production' on prod). We match
  // `!== 'production'` rather than `=== 'preview'` defensively: if that value is
  // ever missing (e.g. an env-pipeline regression), the pill still shows on
  // *.vercel.app instead of silently disappearing — while a build correctly
  // tagged production (incl. its *.vercel.app alias) stays hidden.
  const isPreview =
    isVercel && import.meta.env.REACT_APP_ENVIRONMENT !== 'production';
  const apiOverrideActive = getOverrideApiUrl() != null;
  // The tenant override (DEV_CRM_HOSTNAME / ?crm_hostname=) is only honored by
  // getCrmHostname() when isVercel || isLocalhost. On a deployed custom domain
  // (portal-development.wastehero.io, prod) the tenant is pinned to the URL, so
  // the switcher there would silently no-op — we disable + label it instead.
  const canSwitchTenant = isVercel || isLocalhost;
  // The tenant actually in effect on this host (ignores stale overrides on
  // locked hosts), used for honest display in the pill.
  const effectiveTenant = getCrmHostname();
  const currentTenant = getCurrentTenant();
  const tenantOverrideActive =
    canSwitchTenant && currentTenant !== DEFAULT_TENANT;
  const bypassActive = isDevBypassAuth();
  const visible =
    isDev ||
    isPreview ||
    apiOverrideActive ||
    tenantOverrideActive ||
    bypassActive ||
    unlocked;

  if (!visible) return null;

  const matched = matchEnv(baseURL);
  const previewNumber = isPreviewUrl(baseURL)
    ? getPreviewNumber(baseURL)
    : null;
  const short =
    previewNumber != null
      ? `preview-${previewNumber}`
      : (matched?.short ?? 'custom');
  const tone: Tone = matched?.tone ?? 'danger';

  const apply = () => {
    const cleanedTenant = tenantInput.trim();
    let needsReload = false;

    // Bypass flag changes require a reload either way — the User/Property
    // stores read it on render, but the augmentation runs at query-load.
    if (bypassInput !== bypassActive) {
      setDevBypassAuth(bypassInput);
      needsReload = true;
    }

    if (selected !== baseURL) {
      // overrideApiUrl reloads — set tenant first (only on hosts that honor it;
      // on locked hosts getCrmHostname ignores DEV_CRM_HOSTNAME, so writing it
      // would just leave dead state behind).
      if (canSwitchTenant) {
        if (cleanedTenant && cleanedTenant !== DEFAULT_TENANT) {
          localStorage.setItem(TENANT_LS_KEY, cleanedTenant);
        } else if (cleanedTenant === '' || cleanedTenant === DEFAULT_TENANT) {
          localStorage.removeItem(TENANT_LS_KEY);
        }
      }
      // Backend swap invalidates any citizen token from the previous backend.
      clearCitizenAuth();
      overrideApiUrl(selected);
      return;
    }

    if (canSwitchTenant) {
      if (cleanedTenant === '' || cleanedTenant === DEFAULT_TENANT) {
        if (localStorage.getItem(TENANT_LS_KEY) != null) {
          localStorage.removeItem(TENANT_LS_KEY);
          clearCitizenAuth();
          needsReload = true;
        }
      } else if (cleanedTenant !== currentTenant) {
        localStorage.setItem(TENANT_LS_KEY, cleanedTenant);
        clearCitizenAuth();
        needsReload = true;
      }
    }

    if (needsReload) {
      window.location.reload();
    } else {
      setOpen(false);
    }
  };

  const resetAll = () => {
    localStorage.removeItem(LS_API_URL);
    localStorage.removeItem(TENANT_LS_KEY);
    localStorage.removeItem(LS_DEV_BYPASS_AUTH);
    clearCitizenAuth();
    window.location.reload();
  };

  const options = ENV_OPTIONS.filter(
    (opt) => opt.value && opt.value !== 'not-set'
  );

  const anyOverride = apiOverrideActive || tenantOverrideActive || bypassActive;

  return (
    <div className="wh-ui">
      <button
        type="button"
        onClick={() => {
          setSelected(baseURL);
          setTenantInput(getCrmHostname());
          setBypassInput(isDevBypassAuth());
          setOpen(true);
        }}
        className={cn(
          'fixed bottom-4 right-4 z-[60] inline-flex max-w-[80vw] items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] font-medium tracking-wide shadow-sm transition hover:shadow-md',
          TONE_CLASSES[tone]
        )}
        aria-label={`Current API backend: ${short}`}
        title={`API: ${baseURL}\nTenant: ${effectiveTenant}`}
      >
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80',
            tone === 'warn' && 'animate-pulse'
          )}
        />
        <span className="truncate">{short}</span>
        {apiOverrideActive && (
          <span className="shrink-0 rounded-sm border border-current px-1 text-[9px] uppercase opacity-80">
            override
          </span>
        )}
        {bypassActive && (
          <span className="shrink-0 rounded-sm border border-current px-1 text-[9px] uppercase opacity-80">
            bypass
          </span>
        )}
        {tenantOverrideActive && (
          <span className="min-w-0 truncate opacity-80">
            · {subdomain(effectiveTenant)}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dev environment</DialogTitle>
            <DialogDescription>
              Switch the backend and tenant this tab talks to. The page reloads
              after applying. Both overrides live in this browser&apos;s{' '}
              <code className="font-mono">localStorage</code> only.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Backend
            </p>
            <RadioGroup
              value={isPreviewUrl(selected) ? APIURLS.preview : selected}
              onValueChange={(next) => {
                if (next === APIURLS.preview && isPreviewUrl(baseURL)) {
                  setSelected(baseURL);
                } else {
                  setSelected(next);
                }
              }}
              className="gap-2"
            >
              {options.map((opt) => {
                const isPreview = opt.short === 'preview';
                const active = isPreview
                  ? isPreviewUrl(selected)
                  : selected === opt.value;
                const previewN = isPreview
                  ? (getPreviewNumber(selected) ?? 1)
                  : null;
                const displayUrl =
                  isPreview && active
                    ? buildPreviewUrl(previewN ?? 1)
                    : opt.value;
                return (
                  <Label
                    key={opt.value}
                    htmlFor={`dev-backend-${opt.short}`}
                    className={cn(
                      'flex w-full min-w-0 cursor-pointer items-start gap-3 overflow-hidden rounded-md border border-border px-3 py-2.5 transition hover:bg-accent',
                      active && 'border-foreground/30 bg-accent/40'
                    )}
                  >
                    <RadioGroupItem
                      id={`dev-backend-${opt.short}`}
                      value={opt.value}
                      className="mt-0.5 shrink-0"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm font-medium leading-none">
                        {opt.label}
                      </span>
                      <span className="mt-1 truncate font-mono text-xs text-muted-foreground">
                        {displayUrl}
                      </span>
                      {isPreview && active && (
                        <span className="mt-2 flex flex-wrap gap-1">
                          {Array.from(
                            { length: PREVIEW_COUNT },
                            (_, i) => i + 1
                          ).map((n) => {
                            const isCurrent = previewN === n;
                            return (
                              <button
                                key={n}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelected(buildPreviewUrl(n));
                                }}
                                className={cn(
                                  'inline-flex h-7 w-7 items-center justify-center rounded-md border font-mono text-xs transition',
                                  isCurrent
                                    ? 'border-foreground/40 bg-foreground text-background'
                                    : 'border-border bg-background text-foreground hover:bg-accent'
                                )}
                                aria-label={`Preview server ${n}`}
                              >
                                {n}
                              </button>
                            );
                          })}
                        </span>
                      )}
                    </span>
                  </Label>
                );
              })}
            </RadioGroup>
          </div>

          <Separator />

          <div
            className={cn(
              'flex flex-col gap-2',
              !canSwitchTenant && 'opacity-60'
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Tenant
              </p>
              {canSwitchTenant && tenantInput !== DEFAULT_TENANT && (
                <button
                  type="button"
                  onClick={() => setTenantInput(DEFAULT_TENANT)}
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  reset to default
                </button>
              )}
            </div>
            <Input
              id="dev-tenant"
              value={tenantInput}
              onChange={(e) => setTenantInput(e.target.value)}
              placeholder={DEFAULT_TENANT}
              disabled={!canSwitchTenant}
              className="font-mono text-xs"
              spellCheck={false}
              autoComplete="off"
            />
            <div className="flex flex-wrap gap-1.5">
              {TENANT_EXAMPLES.map((host) => {
                const active = tenantInput === host;
                return (
                  <button
                    key={host}
                    type="button"
                    disabled={!canSwitchTenant}
                    onClick={() => setTenantInput(host)}
                    className={cn(
                      'rounded-full border px-2 py-0.5 font-mono text-[11px] transition disabled:cursor-not-allowed',
                      active
                        ? 'border-foreground/40 bg-accent text-foreground'
                        : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground'
                    )}
                  >
                    {host}
                  </button>
                );
              })}
            </div>
            {canSwitchTenant ? (
              <p className="text-xs text-muted-foreground">
                Sent as <code className="font-mono">crm_hostname</code>.
                Defaults to <code className="font-mono">{DEFAULT_TENANT}</code>.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Locked to this domain (
                <code className="font-mono">{effectiveTenant}</code>) on
                deployed builds — the override is ignored here. Switch tenants
                from a preview (<code className="font-mono">*.vercel.app</code>)
                or localhost build.
              </p>
            )}
          </div>

          <Separator />

          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Auth bypass
            </p>
            <Label
              htmlFor="dev-bypass-auth"
              className={cn(
                'flex w-full cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-2.5 transition hover:bg-accent',
                bypassInput && 'border-foreground/30 bg-accent/40'
              )}
            >
              <Checkbox
                id="dev-bypass-auth"
                checked={bypassInput}
                onCheckedChange={(c) => setBypassInput(c === true)}
                className="mt-0.5 shrink-0"
              />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-medium leading-none">
                  Skip MitID and use mock properties
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  Forces the user non-anonymous and intercepts{' '}
                  <code className="font-mono">property/</code> calls with
                  fixtures. Use for QA on tenants without working auth.
                </span>
              </span>
            </Label>
          </div>

          <DialogFooter className="gap-2 sm:justify-between sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={resetAll}
              disabled={!anyOverride}
              className="sm:mr-auto"
            >
              Reset all
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={apply}>
                Apply
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
